import { useState } from "react";
import { db, storage } from "../firebase";
import { deleteDoc, doc as firestoreDoc } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { showSuccessToast, showErrorToast } from "../toastmsg";

interface UseDeleteOptions<T> {
  collectionName: string;
  onSuccess?: () => void | Promise<void>;
  itemNameField?: keyof T;
  storageField?: keyof T;
}

/**
 * A custom hook to handle deletion of documents from Firestore and optional files from Storage.
 *
 * @param options - Configuration options for the delete operation.
 * @returns State and functions to manage the deletion process and confirmation dialog.
 */
export function useDelete<T extends { id?: string; ID?: string }>(
  options: UseDeleteOptions<T>,
) {
  const {
    collectionName,
    onSuccess,
    itemNameField = "name" as keyof T,
    storageField,
  } = options;

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  /**
   * Executes the deletion process.
   */
  const handleDelete = async () => {
    const id = itemToDelete?.id || itemToDelete?.ID;
    if (!id) return;

    try {
      // 1. Storage cleanup if needed
      if (storageField && itemToDelete[storageField]) {
        try {
          const fileUrl = itemToDelete[storageField] as unknown as string;
          // Only attempt delete if fileUrl is a string and likely a URL
          if (typeof fileUrl === "string" && fileUrl.startsWith("http")) {
            const fileRef = ref(storage, fileUrl);
            await deleteObject(fileRef);
          }
        } catch (storageError) {
          // Log the error but continue with document deletion
          console.error(
            `Failed to delete file from storage for ${collectionName}:`,
            storageError,
          );
        }
      }

      // 2. Firestore document deletion
      await deleteDoc(firestoreDoc(db, collectionName, id));

      // 3. Success Toast
      const itemName =
        (itemToDelete[itemNameField] as unknown as string) || "Item";
      showSuccessToast(`${itemName} deleted successfully`);

      // 4. Success callback (e.g., refresh data)
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error(`Failed to delete item from ${collectionName}:`, error);
      const itemName =
        (itemToDelete[itemNameField] as unknown as string) || "Item";
      showErrorToast(`Failed to delete ${itemName}`);
    } finally {
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }
  };

  /**
   * Opens the delete confirmation dialog for a specific item.
   * @param item - The item to be deleted.
   */
  const confirmDelete = (item: T) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  /**
   * Closes the delete confirmation dialog without deleting.
   */
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  return {
    showDeleteDialog,
    setShowDeleteDialog,
    itemToDelete,
    setItemToDelete,
    confirmDelete,
    cancelDelete,
    handleDelete,
  };
}
